import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardBody, CardHeader, Button, Input } from '../../components/ui';
import { organizationAPI } from '../../api';
import { useToast } from '../../contexts/ToastContext';
import { copyToClipboard } from '../../utils/clipboard';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const FIELD_LABELS = {
  linkedin: 'LinkedIn Custom Field ID',
  summary: 'Summary Custom Field ID',
  zamanat_lead_id: 'Zamanat Lead ID Custom Field ID',
  platform_source: 'Platform Source Custom Field ID',
};

const defaultConfig = {
  enabled: false,
  api_key: false,
  signature_key: false,
  sync_enabled: false,
  custom_fields: {
    linkedin: '',
    summary: '',
    zamanat_lead_id: '',
    platform_source: '',
  },
};

const CloseIOConfigTab = ({ orgId, org }) => {
  const toast = useToast();
  const [config, setConfig] = useState(defaultConfig);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Form state — credentials are write-only inputs
  const [form, setForm] = useState({
    enabled: false,
    api_key: '',
    signature_key: '',
    sync_enabled: false,
    custom_fields: {
      linkedin: '',
      summary: '',
      zamanat_lead_id: '',
      platform_source: '',
    },
  });

  const webhookUrl = org?.slug
    ? `${API_BASE_URL}/api/v1/webhooks/close-io/${org.slug}`
    : '';

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    try {
      const data = await organizationAPI.getCloseioConfig(orgId);
      setConfig(data);
      setForm((prev) => ({
        ...prev,
        enabled: data.enabled,
        sync_enabled: data.sync_enabled,
        custom_fields: {
          linkedin: data.custom_fields?.linkedin || '',
          summary: data.custom_fields?.summary || '',
          zamanat_lead_id: data.custom_fields?.zamanat_lead_id || '',
          platform_source: data.custom_fields?.platform_source || '',
        },
      }));
    } catch {
      toast.error('Failed to load Close.io configuration');
    } finally {
      setLoading(false);
    }
  }, [orgId, toast]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        enabled: form.enabled,
        sync_enabled: form.sync_enabled,
        custom_fields: form.custom_fields,
      };
      // Only send credentials if the user typed something
      if (form.api_key) payload.api_key = form.api_key;
      if (form.signature_key) payload.signature_key = form.signature_key;

      const updated = await organizationAPI.updateCloseioConfig(orgId, payload);
      setConfig(updated);
      setForm((prev) => ({ ...prev, api_key: '', signature_key: '' }));
      toast.success('Close.io configuration saved');
    } catch {
      toast.error('Failed to save Close.io configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleCopyWebhook = async () => {
    const ok = await copyToClipboard(webhookUrl);
    if (ok) {
      toast.success('Webhook URL copied');
    } else {
      toast.error('Failed to copy');
    }
  };

  if (loading) {
    return (
      <Card>
        <CardBody>
          <p className="text-center text-gray-500 dark:text-gray-400 py-4">Loading…</p>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <h2 className="text-base font-semibold text-gray-900 dark:text-white">Close.io</h2>
      </CardHeader>
      <CardBody className="space-y-6">

        {/* Enable integration toggle */}
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm font-medium text-gray-900 dark:text-white">Enable integration</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              When disabled, webhook requests to this org's URL are silently dropped.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setForm((f) => ({ ...f, enabled: !f.enabled }))}
            className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none ${
              form.enabled ? 'bg-primary' : 'bg-gray-300 dark:bg-gray-600'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                form.enabled ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>

        {form.enabled && (
          <>
            <hr className="border-gray-200 dark:border-gray-700" />

            {/* Webhook URL */}
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">Inbound Webhook URL</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                Point your Close.io webhook at this URL to route leads into this organization.
              </p>
              <div className="flex gap-2 items-center">
                <code className="flex-1 min-w-0 text-xs bg-gray-100 dark:bg-gray-800 rounded px-3 py-2 break-all select-all">
                  {webhookUrl}
                </code>
                <Button size="sm" variant="secondary" onClick={handleCopyWebhook} className="shrink-0">
                  Copy
                </Button>
              </div>
            </div>

            {/* API Key */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Close.io API Key
                {config.api_key && (
                  <span className="ml-2 text-xs text-green-600 dark:text-green-400">(configured)</span>
                )}
              </label>
              <Input
                type="password"
                placeholder={config.api_key ? 'Leave blank to keep existing key' : 'Enter API key'}
                value={form.api_key}
                onChange={(e) => setForm((f) => ({ ...f, api_key: e.target.value }))}
                autoComplete="new-password"
              />
            </div>

            {/* Signature Key */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Webhook Signature Key
                {config.signature_key && (
                  <span className="ml-2 text-xs text-green-600 dark:text-green-400">(configured)</span>
                )}
              </label>
              <Input
                type="password"
                placeholder={config.signature_key ? 'Leave blank to keep existing key' : 'Enter signature key (required for webhook verification)'}
                value={form.signature_key}
                onChange={(e) => setForm((f) => ({ ...f, signature_key: e.target.value }))}
                autoComplete="new-password"
              />
              {!config.signature_key && (
                <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
                  Required — webhook requests will be rejected (401) until a key is set.
                </p>
              )}
            </div>

            {/* Outbound sync toggle */}
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-white">Outbound sync</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Sync manually created leads back to Close.io.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setForm((f) => ({ ...f, sync_enabled: !f.sync_enabled }))}
                className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none ${
                  form.sync_enabled ? 'bg-primary' : 'bg-gray-300 dark:bg-gray-600'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                    form.sync_enabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            <hr className="border-gray-200 dark:border-gray-700" />

            {/* Custom Field IDs */}
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">Custom Field IDs</p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                Map Close.io contact-level custom field IDs (e.g. <code className="text-xs bg-gray-100 dark:bg-gray-800 rounded px-1">cf_xxx</code>). Leave blank to use global environment variables.
              </p>
              <div className="space-y-3">
                {Object.keys(FIELD_LABELS).map((key) => (
                  <div key={key}>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {FIELD_LABELS[key]}
                    </label>
                    <Input
                      placeholder="cf_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                      value={form.custom_fields[key]}
                      onChange={(e) =>
                        setForm((f) => ({
                          ...f,
                          custom_fields: { ...f.custom_fields, [key]: e.target.value },
                        }))
                      }
                    />
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        <div className="flex justify-end pt-2">
          <Button onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save Configuration'}
          </Button>
        </div>

      </CardBody>
    </Card>
  );
};

export default CloseIOConfigTab;
